import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stepper, Step, StepLabel, Button, Paper, CircularProgress,
  Alert, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { leesJson, meldFout } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';

import ScriptInput from './ScriptInput';
import BreakdownView from './BreakdownView';
import ShotlistView from './ShotlistView';
import PlanningView from './PlanningView';

const STEPS = ['Script', 'Breakdown', 'Shotlist', 'Planning'];

const STEP_MAP = { draft: 0, breakdown: 1, shotlist: 2, planned: 3 };

const ScriptBreakdown = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const fetchProjects = useCallback(async () => {
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects`);
      const data = await leesJson(resp, 'Projecten laden');
      setProjects(data);
    } catch (e) {
      meldFout(e, 'Projecten laden');
      setProjects([]);
    }
  }, []);

  const fetchProjectData = useCallback(async (projectId, resetStep = true) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${projectId}`);
      const data = await leesJson(resp, 'Project laden');
      setProjectData(data);
      if (resetStep) {
        setActiveStep(STEP_MAP[data.status] || 0);
      }
    } catch (e) {
      setError(e.message || 'Kon project niet laden');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    if (selectedProject) fetchProjectData(selectedProject, true);
  }, [selectedProject, fetchProjectData]);

  // Vanuit een callsheet: briefing crew als script inladen (Joris 18 sep: shotlist uit de briefing).
  const location = useLocation();
  const navigate = useNavigate();
  const prefillGedaan = useRef(false);
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf?.script || prefillGedaan.current) return;
    prefillGedaan.current = true;
    (async () => {
      try {
        const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: pf.title || 'Shotlist' }),
        });
        const data = await leesJson(resp, 'Project aanmaken');
        const put = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${data.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: pf.title || 'Shotlist', script_text: pf.script, script_json: null, project_number: pf.projectNumber || '' }),
        });
        if (!put.ok) throw new Error('Script opslaan mislukt');
        await fetchProjects();
        setSelectedProject(data.id);
        toast.success('Briefing als script ingeladen — maak nu de breakdown en shotlist');
      } catch (e) { meldFout(e, 'Briefing inladen'); }
      finally { navigate(location.pathname, { replace: true, state: null }); }
    })();
  }, [location, fetchProjects, navigate]);

  const refreshProject = useCallback(() => {
    if (selectedProject) fetchProjectData(selectedProject, false);
  }, [selectedProject, fetchProjectData]);

  const handleCreateProject = async () => {
    if (!newTitle.trim()) return;
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      const data = await leesJson(resp, 'Project aanmaken');
      setNewDialogOpen(false);
      setNewTitle('');
      await fetchProjects();
      setSelectedProject(data.id);
      toast.success('Project aangemaakt');
    } catch (e) {
      meldFout(e, 'Project aanmaken');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!(await bevestig({ titel: 'Project verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${id}`, { method: 'DELETE' });
      if (selectedProject === id) {
        setSelectedProject(null);
        setProjectData(null);
      }
      await fetchProjects();
      toast.success('Project verwijderd');
    } catch (e) {
      toast.error('Fout bij verwijderen');
    }
  };

  // Project list view
  if (!selectedProject) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
        <PageHeader
          icon="movie_filter"
          title="Script Breakdown"
          subtitle="Script analyse en shot planning"
          accent={{ color: '#635983', bg: '#dbcdff' }}
          rightActions={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewDialogOpen(true)}
              sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
              Nieuw project
            </Button>
          }
        />

        {projects.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Nog geen projecten. Maak een nieuw project aan om te beginnen.</Typography>
          </Paper>
        ) : (
          <Paper>
            <List>
              {projects.map((p) => (
                <ListItem key={p.id} button onClick={() => setSelectedProject(p.id)}
                  sx={{ '&:hover': { bgcolor: 'rgba(124,58,237,0.04)' } }}>
                  <ListItemText
                    primary={p.title}
                    secondary={`Status: ${p.status} | ${new Date(p.updated_at).toLocaleDateString('nl-NL')}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={(e) => { e.stopPropagation(); handleDeleteProject(p.id); }}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Nieuw Script Breakdown Project</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus fullWidth margin="dense" label="Projectnaam"
              value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewDialogOpen(false)}>Annuleren</Button>
            <Button variant="contained" onClick={handleCreateProject}
              sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
              Aanmaken
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Project detail view with stepper
  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="movie_filter"
        title={projectData?.title || 'Laden...'}
        subtitle="Script Breakdown"
        accent={{ color: '#635983', bg: '#dbcdff' }}
        rightActions={
          <Button variant="text" onClick={() => { setSelectedProject(null); setProjectData(null); }}>
            ← Terug
          </Button>
        }
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, idx) => (
            <Step key={label} completed={activeStep > idx}
              onClick={() => { if (idx <= (STEP_MAP[projectData?.status] || 0)) setActiveStep(idx); }}
              sx={{ cursor: idx <= (STEP_MAP[projectData?.status] || 0) ? 'pointer' : 'default' }}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && projectData && (
        <>
          {activeStep === 0 && (
            <ScriptInput
              project={projectData}
              onRefresh={refreshProject}
              onNext={() => setActiveStep(1)}
            />
          )}
          {activeStep === 1 && (
            <BreakdownView
              project={projectData}
              onRefresh={refreshProject}
              onNext={() => setActiveStep(2)}
              onBack={() => setActiveStep(0)}
            />
          )}
          {activeStep === 2 && (
            <ShotlistView
              project={projectData}
              onRefresh={refreshProject}
              onNext={() => setActiveStep(3)}
              onBack={() => setActiveStep(1)}
            />
          )}
          {activeStep === 3 && (
            <PlanningView
              project={projectData}
              onRefresh={refreshProject}
              onBack={() => setActiveStep(2)}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ScriptBreakdown;
